/**
 * glossaryTerms.js
 *
 * Catalogo de terminos financieros clickeables para el Oraculo.
 * Cada palabra subrayada en el texto del Cerebro abre un tooltip con:
 *   - Definicion humana en 2 frases (sin jerga, con analogia)
 *   - Ejemplo concreto con empresas que el usuario ya conoce
 *   - Boton play que reproduce el audio narrado (~10s) — Sarah ElevenLabs
 *
 * Schema por termino:
 *   slug         identificador URL-safe + filename del audio (audio/glosario/{slug}-es.mp3)
 *   term         texto principal a mostrar (UI + matching en el texto)
 *   matches      variaciones a detectar (plurales, singular/plural, capitalización)
 *   category     'basicos' | 'fundamental' | 'tecnico' | 'cerebro' | 'cripto' | 'colombia'
 *   definition   2-3 frases. Espanol latino. Analogia primero, definicion despues.
 *   example      1 frase con empresa/situacion concreta que el usuario conoce.
 *
 * Audio se genera con scripts/generate_glossary_audios.js (flowexpay-investments-api).
 * Texto narrado al usuario = definition + ' Por ejemplo: ' + example.
 *
 * Reglas innegociables del catalogo:
 * - NO jerga sin explicar. Si usas P/E, explica que es.
 * - SIN nombres de inversores externos (Buffett, Munger, Linares, etc) — politica Ivan.
 * - Espanol latino neutro. Sin tildes en codigo (slugs) pero si en definitions.
 * - Analogias cotidianas: castillo, vecino, supermercado, no abstracciones financieras.
 */

export const GLOSSARY_TERMS = [
  // ========== BÁSICOS (25) ==========
  {
    slug: 'accion', term: 'acción', matches: ['accion', 'accion', 'acciones', 'Accion', 'Acciones'],
    category: 'basicos',
    definition: 'Una acción es un pedacito de una empresa. Cuando compras una acción de Apple, te vuelves dueño de una fracción minúscula de Apple, con derecho a tu porción de las ganancias.',
    example: 'Si Apple tiene 15 mil millones de acciones y tú compras una, eres dueño de 1 entre 15 mil millones de la empresa.',
  },
  {
    slug: 'etf', term: 'ETF', matches: ['ETF', 'ETFs', 'etf'],
    category: 'basicos',
    definition: 'Un ETF es una canasta de muchas acciones empaquetadas en un solo producto. Comprar un ETF es como comprar una rebanada de pastel donde cada migaja es una empresa diferente.',
    example: 'El ETF VOO contiene las 500 empresas más grandes de Estados Unidos. Comprando una unidad de VOO automáticamente eres dueño de un pedazo de Apple, Microsoft, Coca-Cola y 497 más.',
  },
  {
    slug: 'bono', term: 'bono', matches: ['bono', 'bonos', 'Bono', 'Bonos'],
    category: 'basicos',
    definition: 'Un bono es un préstamo que tú le haces a un gobierno o empresa. Te prometen devolverte el dinero más intereses en una fecha definida.',
    example: 'Si compras un bono del Tesoro de Estados Unidos a 10 años, le estás prestando dinero al gobierno gringo y te paga intereses periódicos hasta que te devuelve todo en 10 años.',
  },
  {
    slug: 'dividendo', term: 'dividendo', matches: ['dividendo', 'dividendos', 'Dividendo', 'Dividendos'],
    category: 'basicos',
    definition: 'Un dividendo es una porción de las ganancias de la empresa que reparte entre sus dueños. Como cuando una empresa familiar reparte utilidades entre los socios al final del año.',
    example: 'Coca-Cola paga dividendos trimestrales. Si tienes 100 acciones de Coca-Cola, te llegan automáticamente unos dólares cada tres meses a tu cuenta.',
  },
  {
    slug: 'capitalizacion', term: 'capitalización de mercado',
    matches: ['capitalización de mercado', 'capitalizacion de mercado', 'capitalización', 'capitalizacion', 'market cap', 'cap de mercado'],
    category: 'basicos',
    definition: 'La capitalización de mercado es cuánto vale la empresa entera según el precio actual. Se calcula multiplicando el precio de una acción por todas las acciones que existen.',
    example: 'Si Apple tiene 15 mil millones de acciones y cada una cuesta 200 dólares, su capitalización es de 3 billones de dólares. Es la "etiqueta de precio" total de la empresa.',
  },
  {
    slug: 'pe-ratio', term: 'P/E',
    matches: ['P/E', 'P/E ratio', 'price-to-earnings', 'precio sobre utilidad'],
    category: 'fundamental',
    definition: 'El P/E te dice cuántos años de ganancias tardarías en recuperar lo que pagas por la acción. Es el indicador más usado para saber si una empresa está cara o barata.',
    example: 'Si Apple tiene P/E de 30 y Coca-Cola de 25, significa que Apple cotiza más cara relativa a sus ganancias actuales. Si tardarías 30 años de las ganancias de Apple para recuperar lo invertido versus 25 con Coca-Cola.',
  },
  {
    slug: 'volumen', term: 'volumen',
    matches: ['volumen', 'Volumen'],
    category: 'tecnico',
    definition: 'El volumen es cuántas acciones cambiaron de manos en un día. Volumen alto significa mucha actividad e interés; volumen bajo significa que pocos están comprando o vendiendo.',
    example: 'Si Tesla mueve 100 millones de acciones en un día normal pero hoy mueve 500 millones, algo importante está pasando — buena o mala noticia, vale la pena investigar.',
  },
  {
    slug: 'spread', term: 'spread',
    matches: ['spread', 'Spread'],
    category: 'tecnico',
    definition: 'El spread es la diferencia entre el precio al que puedes comprar y el precio al que puedes vender la misma acción en ese momento. Es el "peaje" implícito de cada operación.',
    example: 'Si una acción se compra a 100.05 dólares pero se vende a 100.00 dólares, el spread es 5 centavos. Acciones populares como Apple tienen spreads pequeñísimos; empresas raras pueden tener spreads grandes.',
  },
  {
    slug: 'liquidez', term: 'liquidez',
    matches: ['liquidez', 'líquido', 'liquido', 'líquida', 'liquida'],
    category: 'basicos',
    definition: 'Liquidez significa qué tan rápido puedes vender algo sin descuento. El efectivo es lo más líquido del mundo; una casa es muy poco líquida porque tarda meses en venderse.',
    example: 'Las acciones de Apple son extremadamente líquidas: puedes vender mil acciones en un segundo al precio del mercado. Una acción de una empresa pequeña olvidada puede tardar días en venderse y al precio que sea.',
  },
  {
    slug: 'volatilidad', term: 'volatilidad',
    matches: ['volatilidad', 'volátil', 'volatil'],
    category: 'tecnico',
    definition: 'Volatilidad es qué tanto sube y baja el precio de algo. Una montaña rusa tiene mucha volatilidad; un piso fijo, ninguna.',
    example: 'Tesla es altamente volátil — puede subir 15% un día y bajar 10% al siguiente. Los bonos del gobierno son muy poco volátiles — se mueven pequeñísimas fracciones cada día.',
  },
  {
    slug: 'comision', term: 'comisión',
    matches: ['comisión', 'comision', 'comisiones', 'comisiones'],
    category: 'basicos',
    definition: 'La comisión es lo que cobra el broker por ejecutar tu compra o venta. Es como la tarifa que cobra el taxi por llevarte de un lado al otro.',
    example: 'FlowexPay cobra una comisión escalonada según el monto invertido. Mientras más grande la operación, menor el porcentaje. En este momento parte desde el 1% para montos pequeños.',
  },
  {
    slug: 'fraccion', term: 'fracción',
    matches: ['fracción', 'fraccion', 'acción fraccionada', 'accion fraccionada', 'fraccionada', 'fraccionadas'],
    category: 'basicos',
    definition: 'Una fracción te permite comprar un pedacito de una acción cara sin comprarla entera. Es como comprar la mitad de una pizza si la pizza completa es muy grande para ti.',
    example: 'Una sola acción de Berkshire Hathaway cuesta más de 600 mil dólares. Con compra fraccionada en FlowexPay puedes comprar 50 dólares de Berkshire y obtener una fracción minúscula pero real de la empresa.',
  },
  {
    slug: 'broker', term: 'broker',
    matches: ['broker', 'brokers', 'corredor', 'corredora'],
    category: 'basicos',
    definition: 'El broker es el intermediario que ejecuta tus compras y ventas en la bolsa. Tú no llamas directo a Apple para comprarle acciones — un broker lo hace por ti.',
    example: 'FlowexPay usa a Alpaca como broker regulado en Estados Unidos. Cuando tú compras AAPL en la app, Alpaca lleva tu orden a la bolsa de Nueva York en segundos.',
  },
  {
    slug: 'custodia', term: 'custodia',
    matches: ['custodia', 'custodio', 'custodiar'],
    category: 'basicos',
    definition: 'La custodia es quién guarda tus activos físicamente o digitalmente. Como tener tu dinero en una caja fuerte de un banco — no lo tienes en tu casa, lo guarda alguien por ti.',
    example: 'Tus dólares en FlowexPay están custodiados en Fireblocks, una institución que guarda criptoactivos para bancos y fondos institucionales. No es FlowexPay quien tiene la llave de tus dólares.',
  },
  {
    slug: 'portafolio', term: 'portafolio',
    matches: ['portafolio', 'portafolios', 'portfolio'],
    category: 'basicos',
    definition: 'Un portafolio es la mezcla total de inversiones que tienes. Como una canasta donde guardas distintas frutas — algunas que duran mucho, otras que dan dulzura inmediata.',
    example: 'Un portafolio Explorer en FlowexPay combina acciones de Estados Unidos, mercados emergentes, oro y bonos — una mezcla pensada para crecer pero protegida.',
  },
  {
    slug: 'diversificacion', term: 'diversificación',
    matches: ['diversificación', 'diversificacion', 'diversificar', 'diversificado', 'diversificada'],
    category: 'basicos',
    definition: 'Diversificación es no poner todos los huevos en la misma canasta. En vez de invertir todo en una acción, repartes entre varias para que si una cae las otras la sostengan.',
    example: 'Si solo tienes acciones de Tesla y Tesla cae 40%, te golpea fuerte. Si tienes Tesla, Apple, Microsoft, oro y bonos, una caída de Tesla la amortiguan las otras posiciones.',
  },
  {
    slug: 'renta-fija', term: 'renta fija',
    matches: ['renta fija', 'Renta Fija'],
    category: 'basicos',
    definition: 'Renta fija es cuando sabes desde el principio cuánto te van a pagar de rendimiento. Como un CDT del banco — te dicen el porcentaje exacto antes de invertir.',
    example: 'Un bono del Tesoro a 5 años que paga 4% es renta fija — vas a recibir ese 4% sin importar lo que pase con la bolsa.',
  },
  {
    slug: 'renta-variable', term: 'renta variable',
    matches: ['renta variable', 'Renta Variable'],
    category: 'basicos',
    definition: 'Renta variable es cuando no sabes de antemano cuánto ganarás — depende de cómo le vaya a la empresa o mercado. Las acciones son el ejemplo clásico.',
    example: 'Comprar acciones de Tesla es renta variable: puedes ganar 50% si sube, o perder 30% si baja. Nadie te garantiza nada antes.',
  },
  {
    slug: 'ipo', term: 'IPO',
    matches: ['IPO', 'IPOs', 'salida a bolsa', 'oferta pública inicial', 'oferta publica inicial'],
    category: 'basicos',
    definition: 'Un IPO es cuando una empresa privada empieza a vender sus acciones al público por primera vez. Es el día en que cualquier persona puede comprar pedacitos de esa empresa.',
    example: 'Facebook hizo su IPO en 2012 a 38 dólares por acción. Quien compró ese día y aguantó, está hoy con su inversión multiplicada por más de 15 veces.',
  },
  {
    slug: 'split', term: 'split',
    matches: ['split', 'splits', 'división de acciones'],
    category: 'basicos',
    definition: 'Un split es cuando una empresa divide cada acción en varias más pequeñas. Como cortar una pizza grande en porciones más manejables — sigue siendo la misma pizza, solo con más pedazos.',
    example: 'En 2020 Apple hizo un split 4 por 1: cada acción se convirtió en 4 acciones de menor precio. Si tenías una a 500 dólares, terminaste con 4 a 125 dólares — mismo valor total.',
  },
  {
    slug: 'stop-loss', term: 'stop loss',
    matches: ['stop loss', 'stop-loss', 'stoploss'],
    category: 'basicos',
    definition: 'Un stop loss es una orden automática para vender si el precio cae por debajo de un nivel que tú fijas. Como un freno de emergencia para limitar pérdidas.',
    example: 'Si compras Tesla a 200 y pones stop loss en 180, el broker venderá automáticamente cuando llegue a ese precio — limitando tu pérdida al 10%, aunque luego siga cayendo más.',
  },
  {
    slug: 'orden-limite', term: 'orden límite',
    matches: ['orden límite', 'orden limite', 'limit order'],
    category: 'basicos',
    definition: 'Una orden límite te permite fijar el precio máximo al que estás dispuesto a comprar (o mínimo al que estás dispuesto a vender). La operación solo se ejecuta si el mercado llega a ese precio.',
    example: 'Si Apple cotiza a 200 pero tú solo quieres comprar a 190, pones una orden límite a 190. Esa orden espera hasta que el precio baje; si no baja, no se ejecuta.',
  },
  {
    slug: 'orden-mercado', term: 'orden a mercado',
    matches: ['orden a mercado', 'market order', 'orden de mercado'],
    category: 'basicos',
    definition: 'Una orden a mercado se ejecuta inmediatamente al precio que haya en ese momento. No esperas — compras al precio del mercado, sea cual sea.',
    example: 'Si das una orden a mercado de Tesla, te la dan al precio que aparece en pantalla en ese segundo. Es rápido pero no controlas el precio exacto.',
  },
  {
    slug: 'asset-allocation', term: 'asset allocation',
    matches: ['asset allocation', 'asignación de activos', 'asignacion de activos'],
    category: 'basicos',
    definition: 'Asset allocation es cómo divides tu plata entre distintos tipos de inversión — acciones, bonos, oro, efectivo. Es la decisión más importante de toda tu estrategia de inversión.',
    example: 'Una asset allocation conservadora podría ser 30% acciones y 70% bonos. Una agresiva sería 90% acciones y 10% efectivo. Define qué tanto subes y bajas con el mercado.',
  },
  {
    slug: 'compounding', term: 'compounding',
    matches: ['compounding', 'interés compuesto', 'interes compuesto', 'capitalización compuesta'],
    category: 'fundamental',
    definition: 'Compounding es cuando los intereses generan más intereses con el tiempo. Como una bola de nieve que rueda cuesta abajo y se hace cada vez más grande.',
    example: 'Si inviertes mil dólares al 10% anual y reinviertes los rendimientos, en 30 años tienes 17 mil. Pero en 50 años tienes 117 mil. La magia ocurre en los últimos años.',
  },

  // ========== FUNDAMENTAL (15) ==========
  {
    slug: 'moat', term: 'moat',
    matches: ['moat', 'moats'],
    category: 'cerebro',
    definition: 'El moat es la "calle que nadie puede cruzar" — la ventaja competitiva que protege a una empresa de sus rivales. En castillos era el foso de agua que impedía el ataque.',
    example: 'El moat de Apple es su ecosistema: cambiar de iPhone a Android te obliga a perder iCloud, iMessage, AirPods bien sincronizados. La fricción es tan alta que más del 90% de usuarios renuevan dentro del mundo Apple.',
  },
  {
    slug: 'ebitda', term: 'EBITDA',
    matches: ['EBITDA'],
    category: 'fundamental',
    definition: 'EBITDA es cuánto gana la empresa de su operación pura, antes de pagar intereses, impuestos y descontar el desgaste de sus activos. Sirve para comparar la "fuerza operativa" entre empresas.',
    example: 'Microsoft tuvo un EBITDA de más de 130 mil millones de dólares en su último año fiscal. Eso es solo la operación pura — sin contar bancos, gobiernos ni equipos viejos.',
  },
  {
    slug: 'roic', term: 'ROIC',
    matches: ['ROIC', 'retorno sobre capital invertido'],
    category: 'fundamental',
    definition: 'ROIC mide qué tan eficiente es la empresa generando ganancias con cada dólar que tiene invertido. Es la calificación de "qué tan buena es la empresa en hacer dinero con dinero".',
    example: 'Apple tiene ROIC histórico de más del 50%, lo que significa que por cada dólar de capital invertido genera 50 centavos de utilidad al año — un desempeño excepcional.',
  },
  {
    slug: 'fcf', term: 'free cash flow',
    matches: ['free cash flow', 'FCF', 'flujo de caja libre'],
    category: 'fundamental',
    definition: 'El free cash flow es la plata real que sobra al final del año después de operar y reinvertir. Es lo que la empresa puede usar para pagar dividendos, recomprar acciones o guardar.',
    example: 'Apple genera más de 100 mil millones de dólares anuales de free cash flow. Con eso recompra sus propias acciones, paga dividendos y aún le sobra plata en la caja.',
  },
  {
    slug: 'margen-operativo', term: 'margen operativo',
    matches: ['margen operativo', 'margen bruto', 'margen neto', 'márgenes'],
    category: 'fundamental',
    definition: 'El margen es qué porcentaje de las ventas se convierte en ganancia. Un margen alto significa que la empresa cobra mucho más de lo que le cuesta producir su producto.',
    example: 'Software como Microsoft tiene márgenes operativos del 40% o más. Empresas de supermercado como Walmart trabajan con márgenes del 4% — venden mucho pero ganan poco por cada peso.',
  },
  {
    slug: 'beta', term: 'beta',
    matches: ['beta', 'Beta'],
    category: 'fundamental',
    definition: 'Beta mide qué tanto se mueve una acción en comparación con todo el mercado. Beta de 1 = se mueve igual. Beta de 2 = se mueve el doble. Beta de 0.5 = la mitad.',
    example: 'Tesla tiene un beta cercano a 2 — cuando el mercado baja 5%, Tesla suele bajar 10%. Coca-Cola tiene beta bajo cerca de 0.6 — sube y baja menos que el mercado.',
  },
  {
    slug: 'payout-ratio', term: 'payout ratio',
    matches: ['payout ratio', 'payout', 'razón de pago'],
    category: 'fundamental',
    definition: 'El payout ratio es qué porcentaje de sus ganancias reparte la empresa como dividendos en lugar de reinvertirlas. Un payout alto significa que reparte mucho; bajo significa que prefiere reinvertir para crecer.',
    example: 'Coca-Cola reparte como dividendo más del 70% de sus utilidades porque ya es una empresa madura. Amazon no paga dividendo — todo lo reinvierte porque sigue en modo crecimiento.',
  },
  {
    slug: 'growth-vs-value', term: 'growth vs value',
    matches: ['growth', 'value', 'value investing', 'growth investing'],
    category: 'fundamental',
    definition: 'Growth son empresas que crecen rápido sus ventas y utilidades, aunque coticen caras hoy. Value son empresas estables que cotizan baratas comparado con sus ganancias. Dos filosofías diferentes de inversión.',
    example: 'NVIDIA es típica growth — su ingreso se ha multiplicado en pocos años. Berkshire Hathaway es típica value — compra empresas estables que cotizan a múltiplos bajos.',
  },
  {
    slug: 'dividend-aristocrat', term: 'dividend aristocrat',
    matches: ['dividend aristocrat', 'aristocrata de dividendos', 'aristócrata de dividendos'],
    category: 'fundamental',
    definition: 'Un dividend aristocrat es una empresa del S&P 500 que ha aumentado su dividendo todos los años durante al menos 25 años seguidos. Es prueba de consistencia y madurez del negocio.',
    example: 'Coca-Cola, Procter & Gamble y Johnson & Johnson son dividend aristocrats — han subido sus dividendos cada año por más de 50 años, incluso en recesiones.',
  },
  {
    slug: 'blue-chip', term: 'blue chip',
    matches: ['blue chip', 'blue-chip', 'blue chips'],
    category: 'fundamental',
    definition: 'Una blue chip es una empresa enorme, antigua, líder de su industria y financieramente sólida. El término viene del póker, donde las fichas azules son las de mayor valor.',
    example: 'Apple, Microsoft, JPMorgan y Coca-Cola son blue chips. Son las acciones que los grandes fondos institucionales tienen como base de sus portafolios.',
  },
  {
    slug: 'capex', term: 'CAPEX',
    matches: ['CAPEX', 'capex', 'inversión de capital'],
    category: 'fundamental',
    definition: 'CAPEX es la plata que la empresa gasta en fábricas, equipos o tecnología nueva para crecer. Se diferencia de los gastos de operar el día a día — es para construir el futuro.',
    example: 'NVIDIA invierte miles de millones en CAPEX construyendo más capacidad de fabricación de chips. Una empresa de servicios como una consultora invierte poco CAPEX porque su negocio es gente, no fábricas.',
  },
  {
    slug: 'roe', term: 'ROE',
    matches: ['ROE', 'return on equity', 'retorno sobre patrimonio'],
    category: 'fundamental',
    definition: 'ROE mide cuánto gana la empresa con la plata que han puesto sus dueños. Si el ROE es 20%, la empresa genera 20 centavos al año por cada dólar de patrimonio de los accionistas.',
    example: 'Apple tiene ROE superior al 150% — una empresa increíblemente eficiente. Un banco normal tiene ROE de 10-15%; cuando un banco supera 20% sostenido, es excepcional.',
  },
  {
    slug: 'buyback', term: 'buyback',
    matches: ['buyback', 'recompra', 'recompra de acciones', 'share buyback'],
    category: 'fundamental',
    definition: 'Buyback es cuando la empresa usa su propia plata para comprar sus acciones de vuelta del mercado. Es una forma de devolver valor a los accionistas — al haber menos acciones, las que quedan valen más.',
    example: 'Apple recompra acciones por más de 80 mil millones de dólares al año. Si las acciones totales bajaron de 17 mil millones a 15 mil millones por buybacks, cada acción restante representa una mayor porción de la empresa.',
  },
  {
    slug: 'pricing-power', term: 'pricing power',
    matches: ['pricing power', 'poder de precio'],
    category: 'fundamental',
    definition: 'Pricing power es la capacidad de subir precios sin perder clientes. Empresas con pricing power son las que pueden traspasarle la inflación al cliente sin que se vaya con la competencia.',
    example: 'Apple sube el precio del iPhone año tras año y la gente lo sigue comprando. Eso es pricing power brutal — la marca y el ecosistema son tan fuertes que los usuarios aceptan pagar más.',
  },
  {
    slug: 'pe-forward', term: 'forward P/E',
    matches: ['forward P/E', 'P/E forward', 'P/E proyectado'],
    category: 'fundamental',
    definition: 'El forward P/E usa las ganancias esperadas a futuro en vez de las actuales. Sirve para ver si la acción está cara o barata según lo que se cree que va a ganar el próximo año.',
    example: 'Si una empresa tiene P/E actual de 40 pero forward P/E de 25, significa que el mercado espera que sus ganancias crezcan fuerte y por eso paga premium hoy.',
  },

  // ========== TÉCNICO (10) ==========
  {
    slug: 'rsi', term: 'RSI',
    matches: ['RSI', 'índice de fuerza relativa', 'indice de fuerza relativa'],
    category: 'tecnico',
    definition: 'El RSI mide qué tan rápido y fuerte han venido las subidas o bajadas recientes de una acción. Por encima de 70 suele indicar "sobrecomprado"; por debajo de 30, "sobrevendido".',
    example: 'Si NVIDIA tiene RSI de 80, significa que ha subido muy rápido y muchos traders esperan una pausa o corrección. No es una regla absoluta — es una pista, no un mandato.',
  },
  {
    slug: 'sma', term: 'media móvil',
    matches: ['media móvil', 'media movil', 'SMA', 'EMA', 'moving average'],
    category: 'tecnico',
    definition: 'La media móvil es el precio promedio de los últimos N días. Suaviza el ruido de los movimientos diarios y muestra la tendencia general de la acción.',
    example: 'La media móvil de 200 días es una referencia clásica. Cuando el precio cruza por encima, muchos lo leen como señal positiva. Cuando cruza por debajo, como señal negativa.',
  },
  {
    slug: 'soporte', term: 'soporte',
    matches: ['soporte', 'Soporte'],
    category: 'tecnico',
    definition: 'Un soporte es un nivel de precio donde la acción ha rebotado varias veces en el pasado. Es como un piso psicológico donde los compradores suelen entrar.',
    example: 'Si Apple ha rebotado 3 veces en 180 dólares, ese precio se convierte en un soporte importante. Operadores lo vigilan para entrar cuando se acerca.',
  },
  {
    slug: 'resistencia', term: 'resistencia',
    matches: ['resistencia', 'Resistencia'],
    category: 'tecnico',
    definition: 'Una resistencia es un nivel de precio donde la acción se ha frenado varias veces al subir. Es como un techo psicológico donde los vendedores suelen aparecer.',
    example: 'Si Tesla ha intentado superar 300 dólares tres veces sin lograrlo, ese nivel es una resistencia clara. Cuando finalmente la rompe, suele acelerar al alza.',
  },
  {
    slug: 'drawdown', term: 'drawdown',
    matches: ['drawdown', 'caída máxima', 'caida maxima'],
    category: 'tecnico',
    definition: 'El drawdown es la caída desde el punto más alto hasta el más bajo de una inversión. Te dice qué tanto puede doler la peor racha histórica de un activo.',
    example: 'El S&P 500 tuvo un drawdown del 55% en la crisis de 2008. Si tenías un portafolio de un millón, llegó a valer 450 mil en su peor punto antes de recuperarse en años.',
  },
  {
    slug: 'breakout', term: 'breakout',
    matches: ['breakout', 'ruptura'],
    category: 'tecnico',
    definition: 'Un breakout es cuando una acción rompe un nivel importante de resistencia y empieza a moverse con más fuerza. Suele venir con volumen alto y atrae más compradores.',
    example: 'Cuando Tesla rompió por primera vez 100 dólares en 2020 con volumen masivo, fue un breakout que detonó una subida del 700% en los meses siguientes.',
  },
  {
    slug: 'pullback', term: 'pullback',
    matches: ['pullback', 'pull-back', 'pequeña corrección'],
    category: 'tecnico',
    definition: 'Un pullback es una caída temporal en medio de una tendencia alcista. La acción está subiendo en general pero hace una pausa breve antes de seguir.',
    example: 'NVIDIA puede subir 60% en un año pero en medio tener pullbacks del 10%. Los traders disciplinados aprovechan esos pullbacks para comprar a mejor precio.',
  },
  {
    slug: 'maximo-52', term: 'máximo de 52 semanas',
    matches: ['máximo de 52 semanas', 'maximo de 52 semanas', '52-week high', '52 week high'],
    category: 'tecnico',
    definition: 'El máximo de 52 semanas es el precio más alto que tocó la acción en el último año. Sirve como referencia para ver qué tan cerca o lejos del tope reciente está hoy.',
    example: 'Si Apple cotiza a 200 y su máximo de 52 semanas es 210, está a 5% del tope. Si está a 150 y el máximo es 210, ha caído casi 30% del pico — puede ser oportunidad o señal de problema.',
  },
  {
    slug: 'sharpe', term: 'Sharpe ratio',
    matches: ['Sharpe ratio', 'Sharpe', 'ratio de Sharpe'],
    category: 'tecnico',
    definition: 'El Sharpe ratio mide cuánto rendimiento extra te da una inversión por cada unidad de riesgo que tomas. Sharpe alto = mucho rendimiento sin tanto sufrimiento.',
    example: 'Un portafolio con Sharpe de 1 es decente. Sharpe de 2 es excelente. Sharpe negativo significa que estás tomando riesgo para perder plata — pésimo.',
  },
  {
    slug: 'correlacion', term: 'correlación',
    matches: ['correlación', 'correlacion', 'correlacionado', 'correlacionada'],
    category: 'tecnico',
    definition: 'Correlación mide qué tan parecido se mueven dos cosas. Correlación 1 = se mueven idénticas. Correlación 0 = independientes. Correlación negativa = una sube cuando la otra baja.',
    example: 'Las acciones tecnológicas tienen alta correlación entre sí — cuando Apple cae, Microsoft suele caer también. El oro tiene baja correlación con acciones — sirve para diversificar.',
  },

  // ========== CEREBRO 2.0 (10) ==========
  {
    slug: 'data-flywheel', term: 'data flywheel',
    matches: ['data flywheel', 'flywheel'],
    category: 'cerebro',
    definition: 'Un data flywheel es cuando la empresa se vuelve más inteligente cada vez que un cliente la usa, y eso atrae más clientes, que generan más datos, en un círculo virtuoso que es casi imposible de copiar.',
    example: 'Cada foto que tomas con tu iPhone mejora el procesamiento de imagen del siguiente modelo. Cada búsqueda en Google la hace más precisa. Esa retroalimentación es el moat moderno.',
  },
  {
    slug: 'bear-case', term: 'bear case',
    matches: ['bear case', 'tesis bajista', 'argumento en contra'],
    category: 'cerebro',
    definition: 'El bear case es el argumento más fuerte del por qué una empresa podría fracasar. Tomarlo en serio significa enfrentar los riesgos reales, no espantapájaros — solo así puedes invertir con honestidad.',
    example: 'El bear case de Tesla podría ser: la competencia china de BYD, la dependencia de los créditos regulatorios y un Elon Musk distraído. Si entiendes el bear case y aún así inviertes, tu convicción es real.',
  },
  {
    slug: 'pre-mortem', term: 'pre-mortem',
    matches: ['pre-mortem', 'premortem'],
    category: 'cerebro',
    definition: 'Un pre-mortem es imaginar que ya pasó lo peor y preguntar "¿qué falló?". Es una técnica para identificar riesgos antes de que ocurran, mirando hacia atrás desde un futuro hipotético.',
    example: 'Hacer un pre-mortem de Apple en 2031 sería: imagina que cayó 50%, ¿qué pasó? Podría ser regulación antimonopolio en EU, fracaso del Vision Pro, o que China bloquea iPhones. Pensarlo te prepara.',
  },
  {
    slug: 'plataforma', term: 'plataforma',
    matches: ['plataforma', 'plataformas'],
    category: 'cerebro',
    definition: 'En el contexto del Cerebro, una plataforma es un negocio que se refuerza solo: más usuarios atraen más servicios, más servicios atraen más usuarios. El círculo se vuelve imparable.',
    example: 'Apple es plataforma: iPhone trae a iCloud, que trae a Apple Music, que trae a App Store. Cada producto refuerza al siguiente. Por eso cambiar de iPhone se vuelve casi imposible para sus dueños.',
  },
  {
    slug: 'ley-wright', term: 'ley de Wright',
    matches: ['ley de Wright', 'Wright\'s law', 'wright law', 'ley de costos'],
    category: 'cerebro',
    definition: 'La ley de Wright dice que cada vez que se duplica la producción de algo, su costo cae un porcentaje fijo y predecible. Es por qué los autos eléctricos y los paneles solares se han vuelto baratos.',
    example: 'En las baterías de litio el costo cae cerca de 20% cada vez que la producción se duplica. Por eso Tesla puede vender autos más baratos cada año mientras escala la producción.',
  },
  {
    slug: 'curva-adopcion', term: 'curva de adopción',
    matches: ['curva de adopción', 'curva de adopcion'],
    category: 'cerebro',
    definition: 'La curva de adopción es el patrón típico de cómo una tecnología se vuelve mainstream: primero la usan unos pocos pioneros, luego adoptan los rápidos, después la mayoría y al final los rezagados.',
    example: 'La adopción de smartphones siguió esta curva — empezó con BlackBerry para ejecutivos en 2005 y hoy el 90% de los adultos en LATAM tiene uno. Saber en qué punto de la curva está una tecnología es valiosísimo.',
  },
  {
    slug: 'tesis', term: 'tesis de inversión',
    matches: ['tesis de inversión', 'tesis de inversion', 'tesis'],
    category: 'cerebro',
    definition: 'La tesis es la razón concreta por la que crees que una empresa va a generar valor en el futuro. Debe ser específica, falsificable y anclada en hechos — no en sentimientos.',
    example: 'Tesis de Apple: el ecosistema integra hardware + software + servicios de forma que la competencia no puede replicar, generando ingresos recurrentes con márgenes altos y poder de precio.',
  },
  {
    slug: 'catalista', term: 'catalista',
    matches: ['catalista', 'catalistas', 'catalizador', 'catalizadores'],
    category: 'cerebro',
    definition: 'Un catalista es un evento futuro que puede mover el precio de una acción significativamente en una dirección u otra. Identificar catalistas te ayuda a anticipar movimientos.',
    example: 'Catalistas de Apple próximos: lanzamiento del iPhone 17 con IA avanzada, expansión de servicios financieros, crecimiento en India. Cualquiera de esos puede mover la acción.',
  },
  {
    slug: 'optionality', term: 'optionality',
    matches: ['optionality', 'opcionalidad'],
    category: 'cerebro',
    definition: 'Optionality es cuando una empresa tiene varias palancas o productos que podrían explotar en valor sin que estés pagando por ellas hoy. Son apuestas gratis incluidas en la inversión.',
    example: 'Cuando compras Amazon, recibes opcionalidad sobre AWS (cloud), publicidad, satélites Kuiper, robotaxi. Si cualquiera explota, ganas extra sin haber pagado por ello en el precio de hoy.',
  },
  {
    slug: 'tam', term: 'TAM',
    matches: ['TAM', 'mercado total direccionable', 'total addressable market'],
    category: 'cerebro',
    definition: 'TAM es el tamaño total del mercado que la empresa podría capturar si lograra el 100% de market share. Sirve para ver hasta dónde puede crecer un negocio.',
    example: 'TAM del mercado global de publicidad es de más de 800 mil millones de dólares al año. Si Meta ya tiene 130 mil millones, todavía tiene espacio enorme para crecer en ese pastel.',
  },
  {
    slug: 'fsd', term: 'FSD',
    matches: ['FSD', 'Full Self-Driving', 'conduccion autonoma'],
    category: 'cerebro',
    definition: 'FSD significa Full Self-Driving — el sistema de conducción autónoma de Tesla. Es software que permite al carro manejar solo, sin que el conductor humano tome el volante.',
    example: 'Si Tesla logra que FSD funcione perfecto, cada auto que ha vendido se convierte en un robotaxi que genera ingresos 24/7 — pasaría de empresa automotriz a empresa de servicios de transporte global.',
  },
  {
    slug: 'robotaxi', term: 'robotaxi',
    matches: ['robotaxi', 'robotaxis'],
    category: 'cerebro',
    definition: 'Un robotaxi es un vehículo autónomo que opera como taxi sin conductor humano. Te pide servicio por app, te recoge solo, te lleva al destino, cobra automáticamente.',
    example: 'Waymo ya opera robotaxis comerciales en Phoenix y San Francisco. Tesla apuesta a convertir cada auto vendido en robotaxi cuando FSD esté listo — eso convertiría su flota de 6 millones de vehículos en la red de transporte más grande del mundo.',
  },
  {
    slug: 'hyperscaler', term: 'hyperscaler',
    matches: ['hyperscaler', 'hyperscalers'],
    category: 'cerebro',
    definition: 'Los hyperscalers son las 3-4 mega-empresas que dominan la nube global: Amazon Web Services, Microsoft Azure, Google Cloud y Alibaba Cloud. Operan centros de datos a escala planetaria.',
    example: 'NVIDIA vende casi todos sus chips de IA a hyperscalers. Cuando Microsoft anuncia 40 mil millones de capex anual, mucho de eso va directo a comprar chips H100 y H200 de NVIDIA.',
  },
  {
    slug: 'glp1', term: 'GLP-1',
    matches: ['GLP-1', 'GLP1', 'agonistas GLP-1'],
    category: 'fundamental',
    definition: 'GLP-1 es una familia de medicamentos originalmente para diabetes que también causan pérdida significativa de peso. Los más conocidos son Ozempic, Wegovy y Mounjaro.',
    example: 'El mercado global de GLP-1 podría llegar a 100 mil millones de dólares al año. Empresas como Hims se posicionan ofreciendo versiones genéricas compuestas a precios accesibles vía telesalud.',
  },
  {
    slug: 'yoy', term: 'YoY',
    matches: ['YoY', 'year-over-year', 'año contra año'],
    category: 'fundamental',
    definition: 'YoY significa Year-over-Year — compara una cifra del año actual con la misma cifra del año pasado. Es la forma más usada para medir si una empresa está creciendo o decreciendo.',
    example: 'Si NVIDIA reporta ingresos de 35 mil millones este trimestre vs 18 mil millones en el mismo trimestre del año pasado, su crecimiento YoY es de 94%. Eso significa que casi duplicó sus ventas en un año.',
  },
  {
    slug: 'gmv', term: 'GMV',
    matches: ['GMV', 'gross merchandise value'],
    category: 'fundamental',
    definition: 'GMV es el valor total de bienes vendidos a través de una plataforma. No es lo que la empresa ganó, sino lo que pasó por sus rieles. Sirve para medir escala de marketplaces y app stores.',
    example: 'El App Store de Apple procesa más de 90 mil millones de dólares de GMV al año. Apple se queda con una comisión del 15-30% sobre ese GMV — esos son sus ingresos reales por servicios.',
  },
  {
    slug: 'antitrust', term: 'antitrust',
    matches: ['antitrust', 'antimonopolio', 'antimonopolio'],
    category: 'fundamental',
    definition: 'Antitrust son las leyes que evitan que una empresa se vuelva tan dominante que destruya la competencia. Cuando un regulador investiga "antitrust", quiere romper o limitar a una empresa muy grande.',
    example: 'Google enfrenta varios casos antitrust en Estados Unidos y Europa. Si pierde, podría tener que vender Chrome o cambiar cómo monetiza la búsqueda. Lo mismo le pasa a Apple con su control del App Store.',
  },

  // ========== CRIPTO/DEFI (10) ==========
  {
    slug: 'usdc', term: 'USDC',
    matches: ['USDC', 'usdc'],
    category: 'cripto',
    definition: 'USDC es un dólar digital que vive en blockchains. Por cada USDC en circulación hay un dólar real en reserva — es estable y se puede mover instantáneamente por internet.',
    example: 'Cuando recargas en FlowexPay y conviertes a dólares, lo que recibes es USDC custodiado a tu nombre. Se mueve en segundos a cualquier parte del mundo sin pasar por bancos tradicionales.',
  },
  {
    slug: 'stablecoin', term: 'stablecoin',
    matches: ['stablecoin', 'stablecoins'],
    category: 'cripto',
    definition: 'Una stablecoin es una moneda digital diseñada para mantener un precio estable, normalmente atado al dólar. A diferencia de Bitcoin que sube y baja salvajemente, una stablecoin debe valer siempre cerca de un dólar.',
    example: 'USDC y USDT son las dos stablecoins más usadas en el mundo. Mueven cientos de miles de millones de dólares al mes y son el puente entre el dinero tradicional y las criptomonedas.',
  },
  {
    slug: 'blockchain', term: 'blockchain',
    matches: ['blockchain', 'cadena de bloques'],
    category: 'cripto',
    definition: 'Una blockchain es un libro contable público distribuido entre miles de computadoras alrededor del mundo. Cada transacción queda registrada para siempre y nadie puede borrarla.',
    example: 'Cuando envías USDC desde FlowexPay, esa transferencia queda escrita en la blockchain de Solana o Ethereum. Cualquier persona puede verificar que se hizo — no depende de un banco.',
  },
  {
    slug: 'smart-contract', term: 'smart contract',
    matches: ['smart contract', 'smart contracts', 'contrato inteligente'],
    category: 'cripto',
    definition: 'Un smart contract es un programa que vive en una blockchain y ejecuta acuerdos automáticamente cuando se cumplen ciertas condiciones, sin intermediarios humanos.',
    example: 'En Kamino — el protocolo de rendimiento que usa FlowexPay — un smart contract recibe tu USDC, lo presta automáticamente, recoge los intereses y te los devuelve. Todo sin que nadie humano intervenga.',
  },
  {
    slug: 'vault', term: 'vault',
    matches: ['vault', 'vaults', 'bóveda', 'boveda'],
    category: 'cripto',
    definition: 'Un vault es una bóveda digital donde se guardan criptoactivos. En FlowexPay, cada cliente tiene su propio vault custodiado por Fireblocks — separado e identificable.',
    example: 'Cuando depositas dólares en FlowexPay, van a tu vault personal. Es como tener tu propia caja fuerte con tu nombre dentro de un edificio gigante de seguridad — no se mezcla con la plata de otros.',
  },
  {
    slug: 'apy', term: 'APY',
    matches: ['APY'],
    category: 'cripto',
    definition: 'APY es el rendimiento anual que te genera una inversión incluyendo el efecto del interés compuesto. Es la cifra que mejor te dice qué ganarías al año si dejas tu plata trabajando.',
    example: 'Si Kamino te ofrece 8% APY en USDC, mil dólares se convierten en 1080 al final del año (asumiendo que la tasa no cambia). Si retiras a mitad de año, recibes proporcional.',
  },
  {
    slug: 'tvl', term: 'TVL',
    matches: ['TVL'],
    category: 'cripto',
    definition: 'TVL es el total de plata que la gente ha depositado en un protocolo DeFi. Es como el "tamaño de los activos administrados" — sirve para ver qué tan grande y confiable es la plataforma.',
    example: 'Si Kamino tiene TVL de 2 mil millones de dólares, significa que miles de personas en todo el mundo confían en depositar ahí. A mayor TVL, normalmente mayor confianza y resiliencia.',
  },
  {
    slug: 'on-ramp', term: 'on-ramp',
    matches: ['on-ramp', 'on ramp', 'onramp', 'off-ramp', 'off ramp', 'offramp'],
    category: 'cripto',
    definition: 'Un on-ramp es cómo entras del dinero tradicional al mundo cripto. Un off-ramp es lo contrario — cómo sales. Son los puentes entre el sistema bancario y los activos digitales.',
    example: 'FlowexPay es tu on-ramp colombiano: pagas con PSE y entras a dólares digitales. Cuando retiras tu USDC a una cuenta de Bancolombia, eso es un off-ramp.',
  },
  {
    slug: 'defi', term: 'DeFi',
    matches: ['DeFi', 'defi'],
    category: 'cripto',
    definition: 'DeFi son finanzas sin bancos tradicionales. Son protocolos que viven en blockchain donde puedes prestar, pedir prestado o ganar intereses sin pasar por una entidad central.',
    example: 'En DeFi tu plata trabaja 24/7 sin papeleo. Kamino, Aave y Compound son ejemplos de protocolos DeFi donde los usuarios prestan USDC unos a otros vía smart contracts y ganan APY.',
  },
  {
    slug: 'solana', term: 'Solana',
    matches: ['Solana', 'SOL'],
    category: 'cripto',
    definition: 'Solana es una blockchain súper rápida y barata. Las transacciones se confirman en menos de un segundo y cuestan fracciones de centavo — ideal para mover dinero a alta frecuencia.',
    example: 'FlowexPay usa Solana porque enviar 100 mil dólares en USDC cuesta menos de un centavo y tarda 2 segundos. En Ethereum la misma operación podría costar 5 dólares y tardar 30 segundos.',
  },

  // ========== COLOMBIA / TRIBUTARIO (10) ==========
  {
    slug: 'dian', term: 'DIAN',
    matches: ['DIAN'],
    category: 'colombia',
    definition: 'La DIAN es la entidad del gobierno colombiano que cobra los impuestos. Su nombre completo es Dirección de Impuestos y Aduanas Nacionales.',
    example: 'Cuando recargas en FlowexPay, se genera automáticamente una factura electrónica que se reporta a la DIAN. Eso permite que tus operaciones queden bien soportadas para tu declaración de renta.',
  },
  {
    slug: 'iva', term: 'IVA',
    matches: ['IVA'],
    category: 'colombia',
    definition: 'El IVA es el impuesto al valor agregado: un 19% que se cobra sobre la mayoría de bienes y servicios en Colombia. Lo recauda quien vende y se lo transfiere a la DIAN.',
    example: 'Cuando FlowexPay te cobra una comisión, descompone el monto entre la base (lo que se queda como ingreso) y el 19% de IVA (que debe transferirse a la DIAN al final del bimestre).',
  },
  {
    slug: 'ica', term: 'ICA',
    matches: ['ICA'],
    category: 'colombia',
    definition: 'El ICA es el Impuesto de Industria y Comercio. Lo paga la empresa sobre sus ingresos por operar en cierta ciudad. No aparece en la factura del cliente — es responsabilidad de quien vende.',
    example: 'FlowexPay paga ICA en Bogotá cada bimestre. Calcula un porcentaje de sus ingresos por servicios financieros y lo declara ante la Secretaría de Hacienda de Bogotá.',
  },
  {
    slug: 'retencion-fuente', term: 'retención en la fuente',
    matches: ['retención en la fuente', 'retencion en la fuente', 'reteFuente', 'rete fuente'],
    category: 'colombia',
    definition: 'La retención en la fuente es un anticipo de impuesto que se descuenta automáticamente al momento de un pago. La DIAN cobra una parte de tu impuesto antes de que llegues a la declaración anual.',
    example: 'Si una empresa grande te paga 10 millones por un servicio, te retiene en la fuente un porcentaje. Al hacer tu declaración de renta al año siguiente, ese monto ya está abonado a tu favor.',
  },
  {
    slug: 'fic', term: 'FIC',
    matches: ['FIC', 'FICs', 'fondo de inversión colectiva', 'fondo de inversion colectiva'],
    category: 'colombia',
    definition: 'Un FIC es un Fondo de Inversión Colectiva — un vehículo donde muchas personas juntan plata para invertir conjuntamente bajo la administración de un profesional regulado.',
    example: 'El FIC Rentar de Progresión invierte en bienes raíces colombianos. Tú podrías meter 100 mil pesos y obtener tu pedacito de un portafolio inmobiliario diversificado de millones de dólares.',
  },
  {
    slug: 'scb', term: 'SCB',
    matches: ['SCB', 'Sociedad Comisionista de Bolsa', 'comisionista de bolsa'],
    category: 'colombia',
    definition: 'Una SCB es una Sociedad Comisionista de Bolsa — la única entidad legalmente autorizada en Colombia para comprar y vender acciones colombianas por cuenta de terceros.',
    example: 'Progresión es una SCB. Cuando alguien quiere comprar acciones en la Bolsa de Valores de Colombia, tiene que hacerlo a través de una SCB como Progresión, Acciones de Colombia o Casa de Bolsa.',
  },
  {
    slug: 'bvc', term: 'BVC',
    matches: ['BVC', 'Bolsa de Valores de Colombia'],
    category: 'colombia',
    definition: 'La BVC es la Bolsa de Valores de Colombia — el mercado donde se compran y venden las acciones de empresas colombianas que cotizan públicamente.',
    example: 'En la BVC cotizan Ecopetrol, Bancolombia, Grupo SURA, Cemargos y otras 60 empresas locales. Para comprar cualquiera de ellas necesitas una SCB intermediaria.',
  },
  {
    slug: 'colcap', term: 'COLCAP',
    matches: ['COLCAP', 'Colcap'],
    category: 'colombia',
    definition: 'El COLCAP es el índice principal de la bolsa colombiana — agrupa las acciones más líquidas. Si el COLCAP sube 5% en un año, las acciones colombianas en promedio subieron eso.',
    example: 'Es el equivalente colombiano del S&P 500 gringo. Históricamente el COLCAP ha tenido alta volatilidad por su concentración en petroleras, bancos y construcción.',
  },
  {
    slug: 'mandato-colectivo', term: 'mandato colectivo',
    matches: ['mandato colectivo'],
    category: 'colombia',
    definition: 'Un mandato colectivo es una figura legal donde varios pequeños inversionistas autorizan a un tercero a comprar conjuntamente activos que individualmente no podrían acceder.',
    example: 'FlowexPay planea usar mandato colectivo con Progresión: 100 personas que cada una puede invertir 100 mil pesos juntan 10 millones y obtienen acceso a fondos institucionales que requieren tickets mínimos altos.',
  },
  {
    slug: 'declaracion-renta', term: 'declaración de renta',
    matches: ['declaración de renta', 'declaracion de renta', 'declarar renta'],
    category: 'colombia',
    definition: 'La declaración de renta es el documento anual donde reportas a la DIAN cuánto ganaste y cuánto patrimonio tienes. Calcula cuánto impuesto debes pagar después de descuentos.',
    example: 'Si en 2026 tu patrimonio superó cierto umbral o tus ingresos pasaron del tope, debes declarar renta el próximo año. FlowexPay te envía un certificado anual con todos tus movimientos para facilitarte el proceso.',
  },
];

/**
 * Mapa rapido { slug -> termObject } para lookup O(1)
 */
export const GLOSSARY_BY_SLUG = Object.fromEntries(GLOSSARY_TERMS.map(t => [t.slug, t]));

/**
 * Lista plana de todos los matches (variaciones) ordenada por longitud descendente
 * para que el matcher capture "data flywheel" antes que "flywheel" suelto.
 */
export const GLOSSARY_MATCHES = GLOSSARY_TERMS.flatMap(t =>
  t.matches.map(m => ({ match: m, slug: t.slug }))
).sort((a, b) => b.match.length - a.match.length);

/**
 * URL del audio narrado del termino en S3.
 * Schema: audio/glosario/{slug}-es.mp3
 */
export function getGlossaryAudioUrl(slug) {
  const base = import.meta.env.VITE_AUDIO_BASE_URL || 'https://beta.flowexpay.com/audio';
  return `${base}/glosario/${slug}-es.mp3`;
}
